import { Injectable } from "@angular/core";
import { catchError, Observable, throwError } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ChatResponse, AiChatbotResponse, AsyncInvokePayload, AsyncInvokeResponse, JobStatusResponse, AsyncResultResponse, ExecuteSqlPayload, ExecuteSqlResponse } from "../interface/scraii-ai-interface";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AiChatServiceService {
  constructor(private http: HttpClient) { }

  // ✅ Old API endpoint
  private readonly API_URL = environment.aiChatEndPoint;

  // ✅ New AI Chatbot endpoint
  private readonly AI_CHATBOT_URL = environment.aiChatbotEndPoint;

  // Async API endpoints
  private readonly ASYNC_INVOKE_URL = environment.aiAsyncInvokeEndPoint;
  private readonly ASYNC_POLL_URL = environment.aiAsyncPollEndPoint;
  private readonly EXECUTE_SQL_URL = environment.aiExecuteSqlEndPoint;

  sendMessage(prompt: string): Observable<ChatResponse> {
    const payload = { prompt }; // ✅ Payload
    return this.http.post<ChatResponse>(this.API_URL, payload).pipe(
      catchError((err) => {
        console.error("ChatService Error:", err);
        return throwError(() => new Error("Failed to process chat request."));
      })
    );
  }

  // ✅ New method for AI Chatbot endpoint
  sendChatbotMessage(
    user_id: string,
    prompt: string,
    conversation_id: string
  ): Observable<AiChatbotResponse> {
    const payload = { user_id, prompt, conversation_id };

    return this.http.post<AiChatbotResponse>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("AI Chatbot Service Error:", err);
        return throwError(
          () => new Error("Failed to process chatbot request.")
        );
      })
    );
  }

  getChatMessages(conversation_id: string, user_id?: string): Observable<any> {
    const payload = { conversation_id };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Get Chat Messages Error:", err);
        return throwError(() => new Error("Failed to load chat messages."));
      })
    );
  }

  // Execute SQL query and get results
  testSqlQuery(payload: { sqlQuery: string }): Observable<any> {
    const testSqlUrl = environment.apiBaseUrl + "/pss/get-ai-data";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    return this.http.post(testSqlUrl, payload, { headers }).pipe(
      catchError((err) => {
        console.error("Test SQL Query Error:", err);

        let errorMessage = "Failed to execute query.";
        if (err.status === 400) {
          errorMessage = err.error?.error?.message || "400 Bad Request";
        } else if (err.status === 504) {
          errorMessage = "504 Gateway Timeout";
        } else if (err.status === 503) {
          errorMessage = "503 Service Unavailable";
        } else if (err.status === 502) {
          errorMessage = "502 Bad Gateway";
        } else if (err.status === 500) {
          errorMessage = "500 Internal Server Error";
        } else if (err.status === 0) {
          errorMessage = "Network error - Unable to connect to server";
        } else if (err.error?.message) {
          errorMessage = err.error.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  putFeedback(data: {
    prompt: string;
    feedback: string;
    sql_query: string;
    suggested_sql_query: string;
  }): Observable<any> {
    return this.http.put<any>(this.API_URL, data).pipe(
      catchError((err) => {
        console.error("Feedback Error:", err);
        return throwError(() => new Error("Failed to submit feedback."));
      })
    );
  }

  loadChatHistory(user_id: string): Observable<any> {
    const payload = { user_id };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Load Chat History Error:", err);
        return throwError(() => new Error("Failed to load chat history."));
      })
    );
  }

  // Delete a conversation
  deleteConversation(conversation_id: string): Observable<any> {
    const payload = { conversation_id, action: "delete" };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Delete Conversation Error:", err);
        return throwError(() => new Error("Failed to delete conversation."));
      })
    );
  }

  // Async API methods
  submitAsyncJob(payload: AsyncInvokePayload): Observable<AsyncInvokeResponse> {
    return this.http.post<AsyncInvokeResponse>(this.ASYNC_INVOKE_URL, payload).pipe(
      catchError((err) => {
        console.error("Async Job Submit Error:", err);
        return throwError(() => new Error("Failed to submit async job."));
      })
    );
  }

  pollJobStatus(jobid: string): Observable<JobStatusResponse> {
    return this.http.post<JobStatusResponse>(this.ASYNC_POLL_URL, { jobid }).pipe(
      catchError((err) => {
        console.error("Poll Job Status Error:", err);
        return throwError(() => new Error("Failed to poll job status."));
      })
    );
  }

  fetchPresignedResult(url: string): Observable<AsyncResultResponse> {
    return this.http.get<AsyncResultResponse>(url).pipe(
      catchError((err) => {
        console.error("Fetch Presigned Result Error:", err);
        return throwError(() => new Error("Failed to fetch result from presigned URL."));
      })
    );
  }

  executeSql(payload: ExecuteSqlPayload): Observable<ExecuteSqlResponse> {
    return this.http.post<ExecuteSqlResponse>(this.EXECUTE_SQL_URL, payload).pipe(
      catchError((err) => {
        console.error("Execute SQL Error:", err);
        return throwError(() => new Error("Failed to execute SQL query."));
      })
    );
  }
}
